<script setup lang="ts">
import { computed, shallowRef } from "vue";
import { RouterLink } from "vue-router";
import { useI18n } from "vue-i18n";
import { numberOfDaysBetweenTwoDates } from "@bc-library/common";
import { recruitmentApi } from "@/services/api";
import { useUserStore } from "@/config/store/userStore";
import AiScreeningPanel from "@/components/recruitment/AiScreeningPanel.vue";
import BButton from "@/components/common/bButton.vue";
import Collapsible from "@/components/common/Collapsible.vue";
import MultipleImageDisplay from "@/components/common/MultipleImageDisplay.vue";
import ResourceImage from "@/components/resource/resourceImage.vue";
import ScreeningReviewFact from "@/components/recruitment/screening/review/ScreeningReviewFact.vue";
import StarRating from "@/components/starRating/StarRating.vue";
import MaterialSymbolsCommentOutlineRounded from "~icons/material-symbols/comment-outline-rounded";
import MaterialSymbolsEditSquareOutlineRounded from "~icons/material-symbols/edit-square-outline-rounded";
import MaterialSymbolsOpenInNew from "~icons/material-symbols/open-in-new";
import PhCaretDown from "~icons/ph/caret-down";
import PhEnvelopeSimple from "~icons/ph/envelope-simple";
import PhPhone from "~icons/ph/phone";
import SolarUserMinusBroken from "~icons/solar/user-minus-broken";
import SolarUserPlusBoldDuotone from "~icons/solar/user-plus-bold-duotone";
import TargetSchoolIcon from "@/components/resource/degree/TargetSchoolIcon.vue";

const { t, locale } = useI18n();

const emits = defineEmits<{
  openComments: [];
  openEditCandidateInformations: [];
}>();

const userStore = useUserStore();
const application = defineModel<any>({ required: true });
const detailsOpen = shallowRef(false);
const today = new Date();

const displayPriority = computed(() => {
  const priority = application.value?.priority;
  if (priority === 1) return 3;
  if (priority === 2) return 2;
  if (priority === 3) return 1;
  return 0;
});

const numberOfDays = computed(() => {
  return numberOfDaysBetweenTwoDates(today, new Date(application.value._created)) - 1;
});

const isManaging = computed(() => application.value.managedBy.includes(userStore.user._id));

const mainDegree = computed(() => {
  const degrees = application.value.candidate?.degrees || [];
  return degrees.find((current: { isMain?: boolean }) => current.isMain) || degrees[0] || null;
});

const degree = computed(() => {
  return mainDegree.value?.school?.display || mainDegree.value?.degree || "";
});

const isMainSchoolTarget = computed(() => Boolean(mainDegree.value?.school?.isTargetSchool));

const candidateEmail = computed(() => application.value.candidate?.user?.email || "");
const candidatePhone = computed(() => {
  const candidate = application.value.candidate;
  return candidate?.phoneNumber1 || candidate?.phoneNumberPersonal || candidate?.phoneNumber2 || "";
});

const groupLabels = computed(() => {
  return (application.value.candidate?.userGroupsPopulated || [])
    .map((group: { display?: string }) => group.display)
    .filter(Boolean)
    .join(", ");
});

const tagLabels = computed(() => {
  return (application.value.tagsPopulated || [])
    .map((tag: { display?: string }) => tag.display)
    .filter(Boolean)
    .join(", ");
});

const customQuestions = computed(() => {
  return (application.value.customQuestions || []).filter(
    (question: { question?: { resourceDisplay?: string; display?: Record<string, string> } }) => {
      return question?.question?.resourceDisplay || question?.question?.display;
    },
  );
});

const questionLabel = (question: {
  question?: { resourceDisplay?: string; display?: Record<string, string> };
}) => {
  return (
    question.question?.resourceDisplay ||
    question.question?.display?.[locale.value.toUpperCase()] ||
    ""
  );
};

const questionAnswer = (question: { answer?: string }) => {
  if (question.answer === "yes") return t("modules.recruitment.offers.career.yesOption");
  if (question.answer === "no") return t("modules.recruitment.offers.career.noOption");
  return question.answer || "";
};

const sourceLabel = computed(() => {
  const source = application.value.candidateSource;
  const recommended = application.value.recommendedBy;
  if (source === 1)
    return recommended?.website?.display || t("modules.recruitment.screening.websiteNotDefined");
  if (source === 2) return recommended?.company?.display || "";
  if (source === 5) {
    const school = recommended?.degree?.display || recommended?.degreeId?.display || "";
    return `${t(`modules.recruitment.screening.recruitmentSource.${source}`)}${school ? ` - ${school}` : ""}`;
  }
  if (source === 8) return recommended?.offer?.title || "";
  if (source === 0 || source) return t(`modules.recruitment.screening.recruitmentSource.${source}`);
  return "";
});

const sourceResource = computed(() => {
  const source = application.value.candidateSource;
  if (source === 3 || source === 7) return application.value.recommendedBy?.resource || null;
  return null;
});

const officeDisplay = computed(() => application.value.office?.display || "");
const positionDisplay = computed(
  () => application.value.position?.display || application.value.position?.acronym || "",
);
const addToManage = () => {
  const process = application.value;
  const managedIndex = process.managedBy.findIndex(
    (manage: string) => manage === userStore.user._id,
  );
  const managedIndex2 = process.managedByPopulated.findIndex(
    (manage: { _id: string }) => manage._id === userStore.user._id,
  );
  if (managedIndex > -1) {
    process.managedBy.splice(managedIndex, 1);
    if (managedIndex2 > -1) process.managedByPopulated.splice(managedIndex2, 1);
  } else {
    process.managedBy.push(userStore.user._id);
    process.managedByPopulated.push(userStore.user);
  }
  recruitmentApi.partialUpdateRecruitmentProcess(process._id, { managedBy: process.managedBy });
};
</script>

<template>
  <section class="flex min-h-0 min-w-0 flex-col overflow-x-hidden" data-cy="screening-review-identity">
    <div class="flex items-start gap-3">
      <ResourceImage
        disable-link
        is-candidate
        width="w-12"
        height="h-12"
        :resource="application.candidate"
      />
      <div class="min-w-0 flex-1">
        <div class="flex items-start justify-between gap-2">
          <button
            type="button"
            class="min-w-0 text-left"
            data-cy="screening-review-name"
            @click="emits('openEditCandidateInformations')"
          >
            <span class="flex items-center gap-1">
              <span class="truncate text-base font-semibold">
                {{ application.candidate.firstname }} {{ application.candidate.lastname }}
              </span>
              <MaterialSymbolsEditSquareOutlineRounded class="shrink-0 text-sm subtitle" />
            </span>
            <span v-if="positionDisplay" class="mt-0.5 block truncate text-sm subtitle">
              {{ positionDisplay }}
            </span>
          </button>
          <BButton
            size="dense"
            bg="base"
            icon
            :title="t('modules.recruitment.screening.review.openProcess')"
            :to="{ name: 'RecruitmentProcessInterviews', params: { processId: application._id } }"
            data-cy="screening-review-open-process"
          >
            <MaterialSymbolsOpenInNew />
          </BButton>
        </div>
        <div
          v-if="candidateEmail || candidatePhone"
          class="mt-1 flex flex-col gap-0.5 text-xs subtitle"
        >
          <a
            v-if="candidateEmail"
            :href="`mailto:${candidateEmail}`"
            class="inline-flex min-w-0 items-center gap-1 hover:underline"
          >
            <PhEnvelopeSimple class="shrink-0" />
            <span class="truncate">{{ candidateEmail }}</span>
          </a>
          <a
            v-if="candidatePhone"
            :href="`tel:${candidatePhone}`"
            class="inline-flex items-center gap-1 hover:underline"
          >
            <PhPhone class="shrink-0" />
            {{ candidatePhone }}
          </a>
        </div>
      </div>
    </div>

    <dl class="mt-3 hidden xl:block" data-cy="screening-review-facts">
      <ScreeningReviewFact
        :label="t('modules.recruitment.screening.review.fields.received')"
        data-cy="screening-review-fact-received"
      >
        <span :class="numberOfDays > 7 ? 'text-danger-500' : ''">
          {{
            t("modules.recruitment.screening.candidateCard.day", {
              day: numberOfDays,
              count: numberOfDays,
            })
          }}
        </span>
      </ScreeningReviewFact>
      <ScreeningReviewFact
        v-if="officeDisplay"
        :label="t('components.recruitment.candidatePreview.office')"
        data-cy="screening-review-fact-office"
      >
        <span class="inline-flex items-center gap-1.5">
          <img
            v-if="application.countryId"
            class="w-4"
            :src="`/flag_${application.countryId}.webp`"
            :alt="officeDisplay"
          />
          <span class="truncate">{{ officeDisplay }}</span>
        </span>
      </ScreeningReviewFact>
      <ScreeningReviewFact
        v-if="degree"
        :label="t('components.resource.degree.degreeItem.academicInstitution')"
        data-cy="screening-review-fact-degree"
      >
        <span class="inline-flex min-w-0 items-center gap-1">
          <TargetSchoolIcon v-if="isMainSchoolTarget" />
          <span class="line-clamp-2">{{ degree }}</span>
        </span>
      </ScreeningReviewFact>
      <ScreeningReviewFact
        v-if="application.team?.display"
        :label="t('components.recruitment.interviews.candidateInformation.team')"
        data-cy="screening-review-fact-team"
      >
        {{ application.team.display }}
      </ScreeningReviewFact>
      <ScreeningReviewFact
        v-if="sourceLabel"
        :label="t('modules.recruitment.screening.review.fields.source')"
        data-cy="screening-review-fact-source"
      >
        <span class="inline-flex min-w-0 items-center gap-1.5">
          <ResourceImage
            v-if="sourceResource"
            disable-link
            popover
            width="w-5"
            height="h-5"
            :resource="sourceResource"
          />
          <span class="line-clamp-2">{{ sourceLabel }}</span>
        </span>
      </ScreeningReviewFact>
      <ScreeningReviewFact
        v-if="application.howDidYouFindUs"
        :label="t('modules.recruitment.screening.review.fields.foundUs')"
        data-cy="screening-review-fact-foundUs"
      >
        {{ application.howDidYouFindUs.display }}
      </ScreeningReviewFact>
      <ScreeningReviewFact
        v-if="groupLabels"
        :label="t('common.labels.userGroup')"
        data-cy="screening-review-fact-groups"
      >
        <span class="line-clamp-2">{{ groupLabels }}</span>
      </ScreeningReviewFact>
      <ScreeningReviewFact
        v-if="tagLabels"
        :label="t('modules.recruitment.screening.review.fields.tags')"
        data-cy="screening-review-fact-tags"
      >
        <span class="line-clamp-2">{{ tagLabels }}</span>
      </ScreeningReviewFact>
      <ScreeningReviewFact
        v-for="question in customQuestions"
        :key="question._id || question.questionId"
        :label="questionLabel(question)"
        :data-cy="`screening-review-question-${question.questionId || question._id}`"
      >
        {{ questionAnswer(question) }}
      </ScreeningReviewFact>
      <ScreeningReviewFact
        v-if="displayPriority"
        :label="
          t('modules.recruitment.screening.editCandidateInformations.optionalFields.priority')
        "
        data-cy="screening-review-fact-priority"
      >
        <StarRating :rating="displayPriority" read-only :star-size="14" :max-rating="3" />
      </ScreeningReviewFact>
      <ScreeningReviewFact
        v-if="application.aiScreening?.status"
        :label="t('modules.recruitment.aiScreening.title')"
        data-cy="screening-review-fact-aiScreening"
      >
        <AiScreeningPanel :ai-screening="application.aiScreening" compact />
      </ScreeningReviewFact>
      <ScreeningReviewFact
        v-if="application.candidateProcessCount > 0"
        :label="t('modules.recruitment.screening.candidateCard.countProcesses')"
        data-cy="screening-review-fact-processes"
      >
        <RouterLink
          :to="{ name: 'RecruitmentProfile', params: { resourceId: application.candidate._id } }"
          class="text-primary-600 hover:underline"
        >
          {{ application.candidateProcessCount }}
        </RouterLink>
      </ScreeningReviewFact>
    </dl>

    <div class="mt-3 hidden items-center justify-between gap-2 border-t border-base pt-3 xl:flex">
      <div class="flex min-w-0 items-center gap-2">
        <p class="text-xs subtitle">
          {{ t("modules.recruitment.screening.review.fields.managers") }}
        </p>
        <button
          type="button"
          class="cursor-pointer"
          :title="
            isManaging
              ? t('modules.recruitment.screening.review.removeManaging')
              : t('modules.recruitment.screening.review.addManaging')
          "
          data-cy="screening-review-manage"
          @click="addToManage"
        >
          <SolarUserMinusBroken v-if="isManaging" />
          <SolarUserPlusBoldDuotone v-else class="text-lg" />
        </button>
        <MultipleImageDisplay
          :images="
            application.managedByPopulated.map((managed: { picture?: string }) => managed.picture)
          "
          :max="3"
          width="w-6"
          height="h-6"
        />
      </div>
      <BButton
        size="dense"
        icon
        :dot="application.comments.length > 0"
        :bg="application.comments.length ? 'primary' : 'base'"
        :title="t('modules.recruitment.screening.review.fields.comments')"
        data-cy="screening-review-comments"
        @click="emits('openComments')"
      >
        <MaterialSymbolsCommentOutlineRounded />
        <template #dot>
          {{ application.comments.length > 9 ? "9+" : application.comments.length }}
        </template>
      </BButton>
    </div>

    <div class="mt-2 flex flex-wrap items-center gap-2 xl:hidden">
      <span class="text-xs" :class="numberOfDays > 7 ? 'text-danger-500' : 'subtitle'">
        {{
          t("modules.recruitment.screening.candidateCard.day", {
            day: numberOfDays,
            count: numberOfDays,
          })
        }}
      </span>
      <AiScreeningPanel :ai-screening="application.aiScreening" compact />
      <BButton
        size="dense"
        :dot="application.comments.length > 0"
        :bg="application.comments.length ? 'primary' : 'base'"
        icon
        data-cy="screening-review-comments"
        @click="emits('openComments')"
      >
        <MaterialSymbolsCommentOutlineRounded />
        <template #dot>
          {{ application.comments.length > 9 ? "9+" : application.comments.length }}
        </template>
      </BButton>
    </div>

    <Collapsible v-model:open="detailsOpen" class="xl:hidden" remove-trigger>
      <dl class="mt-2">
        <ScreeningReviewFact
          v-if="officeDisplay"
          :label="t('components.recruitment.candidatePreview.office')"
        >
          {{ officeDisplay }}
        </ScreeningReviewFact>
        <ScreeningReviewFact
          v-if="degree"
          :label="t('components.resource.degree.degreeItem.academicInstitution')"
        >
          <span class="inline-flex min-w-0 items-center gap-1">
            <TargetSchoolIcon v-if="isMainSchoolTarget" />
            <span>{{ degree }}</span>
          </span>
        </ScreeningReviewFact>
        <ScreeningReviewFact
          v-if="sourceLabel"
          :label="t('modules.recruitment.screening.review.fields.source')"
        >
          {{ sourceLabel }}
        </ScreeningReviewFact>
        <ScreeningReviewFact v-if="groupLabels" :label="t('common.labels.userGroup')">
          {{ groupLabels }}
        </ScreeningReviewFact>
      </dl>
    </Collapsible>
    <button
      type="button"
      class="mt-1 inline-flex items-center gap-1 text-xs subtitle xl:hidden"
      @click="detailsOpen = !detailsOpen"
    >
      {{
        detailsOpen
          ? t("modules.recruitment.screening.review.hideDetails")
          : t("modules.recruitment.screening.review.showDetails")
      }}
      <PhCaretDown class="transition-transform" :class="{ 'rotate-180': detailsOpen }" />
    </button>
  </section>
</template>
